import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, useScroll } from "framer-motion";
import { Link2, MessageCircle, Send, Heart, MessageSquare, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import AiSummary from "../components/AiSummary";
import Seo from "../components/Seo";
import { LOGO_URL } from "../components/Masthead";
import NewsCard from "../components/NewsCard";
import Reveal from "../components/Reveal";
import { useLang, pickTitle, pickSummary, pickBody, formatDate, formatIST, readMins, timeAgo, SECTIONS, imgSrc } from "../lib/i18n";

function imgError(e) {
  e.currentTarget.onerror = null;
  e.currentTarget.style.display = "none";
}

function PhotoGallery({ images, caption, credit, alt }) {
  const [idx, setIdx] = useState(0);
  const srcs = images.map(imgSrc).filter(Boolean);
  if (!srcs.length) return null;
  const prev = () => setIdx((i) => (i - 1 + srcs.length) % srcs.length);
  const next = () => setIdx((i) => (i + 1) % srcs.length);
  return (
    <figure className="mt-8" data-testid="photo-gallery">
      <div className="relative overflow-hidden border border-ink/15">
        <img
          src={srcs[idx]}
          alt={alt}
          onError={imgError}
          data-testid="gallery-main-image"
          className="w-full aspect-[16/9] object-cover"
        />
        {srcs.length > 1 && (
          <>
            <button
              data-testid="gallery-prev"
              onClick={prev}
              aria-label="पिछली फोटो"
              className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 bg-ink/60 text-paper flex items-center justify-center hover:bg-deepgreen transition-colors"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              data-testid="gallery-next"
              onClick={next}
              aria-label="अगली फोटो"
              className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 bg-ink/60 text-paper flex items-center justify-center hover:bg-deepgreen transition-colors"
            >
              <ChevronRight size={18} />
            </button>
            <span
              data-testid="gallery-counter"
              className="absolute bottom-2 right-2 bg-ink/70 text-paper font-mono text-[10px] px-2 py-1 tracking-widest"
            >
              {idx + 1} / {srcs.length}
            </span>
          </>
        )}
      </div>
      {images.length > 1 && (
        <div className="flex gap-2 mt-2 overflow-x-auto no-scrollbar" data-testid="gallery-thumbs">
          {srcs.map((img, i) => (
            <button
              key={i}
              data-testid={`gallery-thumb-${i}`}
              onClick={() => setIdx(i)}
              aria-label={`फोटो ${i + 1}`}
              className={`shrink-0 border-2 transition-colors ${i === idx ? "border-gold" : "border-transparent opacity-60 hover:opacity-100"}`}
            >
              <img src={img} alt="" onError={imgError} className="w-20 h-14 object-cover" />
            </button>
          ))}
        </div>
      )}
      {caption && (
        <figcaption className="font-mono text-[11px] text-muted-foreground mt-2 border-l-2 border-gold pl-3">
          {caption}
        </figcaption>
      )}
    </figure>
  );
}


function LikeButton({ id, initialLikes }) {
  const { lang } = useLang();
  const [likes, setLikes] = useState(initialLikes || 0);
  const [liked, setLiked] = useState(() => localStorage.getItem(`lok_liked_${id}`) === "1");

  useEffect(() => setLikes(initialLikes || 0), [initialLikes]);

  const like = async () => {
    if (liked) {
      toast.info(lang === "hi" ? "आप पहले ही पसंद कर चुके हैं" : "You already liked this");
      return;
    }
    try {
      const { data } = await api.post(`/articles/${id}/like`);
      setLikes(data.likes);
      setLiked(true);
      localStorage.setItem(`lok_liked_${id}`, "1");
    } catch {
      toast.error(lang === "hi" ? "कुछ गड़बड़ हो गई" : "Something went wrong");
    }
  };

  return (
    <button
      data-testid="like-button"
      onClick={like}
      className={`flex items-center gap-2 border px-4 py-2 font-mono text-xs uppercase tracking-widest transition-all ${
        liked
          ? "bg-brk text-paper border-brk"
          : "border-ink/25 text-ink hover:border-brk hover:text-brk"
      }`}
    >
      <Heart size={15} fill={liked ? "currentColor" : "none"} />
      {liked ? (lang === "hi" ? "पसंद किया" : "Liked") : lang === "hi" ? "पसंद करें" : "Like"}
      {likes > 0 ? ` • ${likes}` : ""}
    </button>
  );
}

function Comments({ id }) {
  const { lang } = useLang();
  const [comments, setComments] = useState([]);
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/articles/${id}/comments`).then((r) => setComments(r.data.comments)).catch(() => {});
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/articles/${id}/comments`, { name: name.trim(), text: text.trim() });
      setComments([data, ...comments]);
      setText("");
      toast.success("आपकी टिप्पणी प्रकाशित हो गई");
    } catch {
      toast.error("टिप्पणी नहीं हो पाई, दोबारा कोशिश करें");
    }
    setBusy(false);
  };

  return (
    <section className="border-t-2 border-ink pt-8 mt-12" data-testid="comments-section">
      <h2 className="font-hindi text-2xl md:text-3xl flex items-center gap-3">
        <MessageSquare size={24} className="text-deepgreen" />
        {lang === "hi" ? "पाठकों की राय" : "Readers' Views"}
        {comments.length > 0 && (
          <span className="font-mono text-sm text-muted-foreground">({comments.length})</span>
        )}
      </h2>
      <span className="block gold-rule w-24 mt-2" />

      <form onSubmit={submit} className="bg-white border border-ink/15 p-5 mt-6" data-testid="comment-form">
        <input
          data-testid="comment-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={lang === "hi" ? "आपका नाम" : "Your name"}
          maxLength={60}
          className="w-full border border-ink/25 bg-paper px-3 py-2 text-sm outline-none focus:border-deepgreen transition-colors"
          required
        />
        <textarea
          data-testid="comment-text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={lang === "hi" ? "अपनी राय लिखें…" : "Share your view…"}
          rows={3}
          maxLength={1000}
          className="w-full border border-ink/25 bg-paper px-3 py-2 text-sm outline-none focus:border-deepgreen transition-colors mt-3"
          required
        />
        <button
          data-testid="comment-submit"
          type="submit"
          disabled={busy}
          className="mt-3 bg-deepgreen text-paper px-6 py-2.5 text-sm font-semibold hover:bg-deepgreen-hover transition-colors disabled:opacity-60"
        >
          {busy ? "…" : lang === "hi" ? "टिप्पणी करें" : "Post comment"}
        </button>
      </form>

      <div className="mt-6 space-y-0" data-testid="comment-list">
        {comments.map((c) => (
          <div key={c.id} className="flex gap-4 border-b border-subtle py-4" data-testid={`comment-${c.id}`}>
            <span className="w-9 h-9 shrink-0 rounded-full bg-deepgreen text-gold-light flex items-center justify-center font-hindi text-sm">
              {c.name.charAt(0).toUpperCase()}
            </span>
            <div className="min-w-0">
              <div className="flex items-baseline gap-2">
                <span className="font-semibold text-sm">{c.name}</span>
                <span className="font-mono text-[10px] text-muted-foreground">{timeAgo(c.created_at, lang)}</span>
              </div>
              <p className="text-sm text-inkgray leading-relaxed mt-1">{c.text}</p>
            </div>
          </div>
        ))}
        {comments.length === 0 && (
          <p className="text-sm text-muted-foreground py-6">
            {lang === "hi" ? "पहली टिप्पणी आप करें।" : "Be the first to comment."}
          </p>
        )}
      </div>
    </section>
  );
}

export default function ArticlePage() {
  const { id } = useParams();
  const { lang, t } = useLang();
  const [a, setA] = useState(null);
  const [related, setRelated] = useState([]);
  const { scrollYProgress } = useScroll();

  useEffect(() => {
    setA(null);
    api.get(`/articles/${id}`).then((r) => setA(r.data)).catch(() => setA(false));
    api.get(`/articles/${id}/related`).then((r) => setRelated(r.data.articles)).catch(() => {});
  }, [id]);

  if (a === null) {
    return <div className="max-w-3xl mx-auto px-4 py-24 text-center font-mono text-sm text-muted-foreground">…</div>;
  }
  if (a === false) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="article-not-found">
        {t.noResults}
      </div>
    );
  }

  const sec = SECTIONS.find((s) => s.id === a.category);
  const isWire = a.source === "rss";
  const body = isWire ? "" : pickBody(a, lang);
  const paragraphs = body.split("\n\n").filter(Boolean);
  const url = window.location.href;
  const srcName = a.source_name || (a.image_credit ?? "") || "RSS";
  const updatedDiff = a.updated_at && a.published_at && a.updated_at.slice(0, 16) !== a.published_at.slice(0, 16);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t.copied);
    } catch {
      toast.error(t.copied);
    }
  };

  return (
    <div data-testid="article-page">
      <Seo
        title={pickTitle(a, lang)}
        description={pickSummary(a, lang)}
        image={a.image_url ? imgSrc(a.image_url) : undefined}
        path={`/article/${a.id}`}
        article={{
          "@context": "https://schema.org",
          "@type": "NewsArticle",
          headline: pickTitle(a, lang),
          description: pickSummary(a, lang),
          ...(a.image_url ? { image: [imgSrc(a.image_url)] } : {}),
          datePublished: a.published_at,
          dateModified: a.updated_at || a.published_at,
          author: { "@type": "Organization", name: "The Vainor India" },
          publisher: {
            "@type": "Organization",
            name: "The Vainor India",
            logo: { "@type": "ImageObject", url: LOGO_URL },
          },
          mainEntityOfPage: url,
        }}
      />
      <motion.div
        data-testid="reading-progress"
        style={{ scaleX: scrollYProgress }}
        className="fixed top-0 left-0 right-0 h-1 bg-gold origin-left z-50"
      />
      <article className="max-w-3xl mx-auto px-4 py-10">
        <Link
          to={`/section/${a.category}`}
          data-testid="article-category"
          className="font-mono text-[11px] uppercase tracking-[0.25em] text-brk hover:text-deepgreen transition-colors"
        >
          {sec ? (lang === "hi" ? sec.hi : sec.en) : a.category}
        </Link>
        <h1 data-testid="article-title" className="font-hindi text-3xl md:text-5xl leading-[1.15] mt-3 text-ink">
          {pickTitle(a, lang)}
        </h1>
        <p className="font-serif italic text-lg md:text-xl text-muted-foreground leading-relaxed mt-4">
          {pickSummary(a, lang)}
        </p>
        <div className="flex flex-wrap items-center justify-between gap-4 border-y border-subtle py-3.5 mt-6">
          <div className="font-mono text-[11px] text-muted-foreground leading-relaxed">
            <span className="text-ink font-semibold">{a.author}</span>
            <span className="block mt-0.5">
              {lang === "hi" ? "प्रकाशित" : "Published"}: {formatIST(a.published_at, lang)} IST
              {updatedDiff && (
                <>
                  {" • "}
                  {lang === "hi" ? "अपडेटेड" : "Updated"}: {formatIST(a.updated_at, lang)} IST
                </>
              )}
              {!isWire && (
                <>
                  {" • "}
                  {readMins(body)} {t.minRead}
                </>
              )}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground mr-1">
              {t.share}
            </span>
            <button
              data-testid="share-copy"
              onClick={copyLink}
              aria-label="copy link"
              className="w-8 h-8 border border-ink/25 flex items-center justify-center hover:bg-deepgreen hover:text-paper hover:border-deepgreen transition-colors"
            >
              <Link2 size={14} />
            </button>
            <a
              data-testid="share-whatsapp"
              href={`https://wa.me/?text=${encodeURIComponent(pickTitle(a, lang) + " " + url)}`}
              target="_blank"
              rel="noreferrer"
              aria-label="whatsapp"
              className="w-8 h-8 border border-ink/25 flex items-center justify-center hover:bg-deepgreen hover:text-paper hover:border-deepgreen transition-colors"
            >
              <MessageCircle size={14} />
            </a>
            <a
              data-testid="share-x"
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(pickTitle(a, lang))}&url=${encodeURIComponent(url)}`}
              target="_blank"
              rel="noreferrer"
              aria-label="x"
              className="w-8 h-8 border border-ink/25 flex items-center justify-center hover:bg-deepgreen hover:text-paper hover:border-deepgreen transition-colors"
            >
              <Send size={14} />
            </a>
          </div>
        </div>

        <PhotoGallery
          images={a.images && a.images.length ? a.images : (a.image_url ? [a.image_url] : [])}
          caption={a.image_caption}
          alt={pickTitle(a, lang)}
        />

        <div className="article-body mt-8" data-testid="article-body">
          {isWire ? (
            <div className="border-l-4 border-gold bg-gold-light/15 p-5" data-testid="wire-summary">
              <p className="text-base leading-relaxed text-inkgray">{pickSummary(a, lang)}</p>
              {a.link && (
                <a
                  href={a.link}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="source-read-full"
                  className="mt-4 inline-flex items-center gap-2 bg-deepgreen text-paper px-5 py-2.5 text-sm font-semibold hover:bg-deepgreen-hover transition-colors"
                >
                  {lang === "hi" ? `पूरी ख़बर पढ़ें — ${srcName}` : `Read full story — ${srcName}`} →
                </a>
              )}
            </div>
          ) : (
            paragraphs.map((p, i) => (
              <p key={i} className={i === 0 ? "drop-cap" : ""}>
                {p}
              </p>
            ))
          )}
          {a.link && (
            <p className="font-mono text-xs text-muted-foreground border-t border-subtle pt-4 mt-6">
              {lang === "hi" ? "स्रोत" : "Source"}:{" "}
              <a href={a.link} target="_blank" rel="noreferrer" className="text-deepgreen underline">
                {srcName}
              </a>
            </p>
          )}
        </div>

        <div className="mt-8">
          <LikeButton id={a.id} initialLikes={a.likes} />
        </div>

        <AiSummary id={a.id} />

        <Comments id={a.id} />
      </article>

      {related.length > 0 && (
        <Reveal>
          <section className="max-w-7xl mx-auto px-4 mt-10 border-t-2 border-ink pt-8" data-testid="related-block">
            <h2 className="font-hindi text-2xl md:text-3xl mb-6">
              {t.related}
              <span className="block gold-rule w-24 mt-2" />
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {related.map((r) => (
                <NewsCard key={r.id} article={r} variant="standard" />
              ))}
            </div>
          </section>
        </Reveal>
      )}
    </div>
  );
}
